/**
 * The send-back's answers in the review (plan v21 B5): under each of the draft's four-part questions, the reviewer
 * keeps its assumption or corrects it; and, in "Since vN", the answers given to the draft before and what the author
 * did with each instruction. Pure views (rendered in tests without providers); lib/imports/send-back holds the logic.
 */
import { Check, X } from "lucide-react";
import { CORRECTION_MAX, type AnswerChoice, type GivenAnswer, type ResponseLine } from "@/lib/imports/send-back";

const INPUT_CLS =
  "w-full rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-on-surface focus:border-brand focus:outline-none disabled:opacity-60";

/** Keep the question's assumption, correct it, or leave it unanswered; a correction says what is right instead. */
export function AnswerControls({
  index,
  about,
  choice,
  onChoose,
  locked,
  lead = "If you send it back:",
}: {
  index: number;
  /** The question in a few words (what the author saw), for the controls' names: its number shows nowhere on screen. */
  about: string;
  choice: AnswerChoice | undefined;
  /** null: leave the question unanswered. */
  onChoose: (index: number, choice: AnswerChoice | null) => void;
  locked: boolean;
  /** What the choices open with: the review's send-back, or (#1428) a revision of the version in use. */
  lead?: string;
}) {
  const name = `answer-${index}`;
  const value = choice ? (choice.keep ? "keep" : "correct") : "";
  const what = about.length > 80 ? `${about.slice(0, 79)}…` : about;
  return (
    <div className="mt-1 flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-on-surface-muted" role="radiogroup" aria-label={`Answer to: ${what}`}>
        <span>{lead}</span>
        {(
          [
            ["", "Leave unanswered"],
            ["keep", "The assumption is right"],
            ["correct", "Correct it"],
          ] as const
        ).map(([v, label]) => (
          <label key={v || "none"} className="flex items-center gap-1">
            <input
              type="radio"
              name={name}
              value={v}
              checked={value === v}
              disabled={locked}
              onChange={() => onChoose(index, v === "" ? null : v === "keep" ? { keep: true } : { keep: false, text: choice && !choice.keep ? choice.text : "" })}
            />
            {label}
          </label>
        ))}
      </div>
      {choice && !choice.keep && (
        <textarea
          className={`${INPUT_CLS} min-h-12`}
          placeholder="What is right instead (the author reads this)"
          value={choice.text}
          maxLength={CORRECTION_MAX}
          disabled={locked}
          onChange={(e) => onChoose(index, { keep: false, text: e.target.value })}
          aria-label={`Correction to: ${what}`}
        />
      )}
    </div>
  );
}

/** The answers given when the draft before was sent back, each with its question. */
export function GivenAnswersView({ answers }: { answers: GivenAnswer[] }) {
  if (answers.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs text-on-surface-muted">Your answers to its questions:</div>
      <ul className="flex flex-col gap-1 text-xs text-on-surface">
        {answers.map((a, i) => (
          <li key={i} className="rounded-md border border-border p-2">
            {(a.saw || a.assume) && (
              <div className="text-on-surface-muted">
                {a.saw}
                {a.saw && a.assume ? "; it assumed " : a.assume ? "It assumed " : ""}
                {a.assume}
              </div>
            )}
            <div>{a.kept ? "Kept: the assumption is right." : `Corrected: ${a.correction}`}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What the author did with each instruction and correction of the send-back, as it says. */
export function ReviewResponseView({ lines }: { lines: ResponseLine[] }) {
  if (lines.length === 0) return null;
  const notDone = lines.filter((l) => !l.done).length;
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs text-on-surface-muted">
        What the brain says it did with the send-back
        {notDone > 0 ? ` (${notDone} not done: check why)` : ""}:
      </div>
      <ul className="flex flex-col gap-1 text-xs text-on-surface">
        {lines.map((l, i) => (
          <li key={i} className="flex items-start gap-1.5">
            {l.done ? (
              <Check className="mt-0.5 size-3.5 shrink-0 text-success" role="img" aria-label="done" />
            ) : (
              <X className="mt-0.5 size-3.5 shrink-0 text-warning" role="img" aria-label="not done" />
            )}
            <span>
              <span className="font-medium">{l.item}</span>
              {l.how && <span className="text-on-surface-muted">: {l.how}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
